import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { IoIosAdd } from "react-icons/io";
import { Dispatch } from "redux";
import arrayMove from "array-move";
import styled from "styled-components";

import { State } from "src/state/ducks";
import * as bakedBreadTagActions from "src/state/ducks/bakedBreadTag/actions";
import { BakedBreadTagEntity } from "src/firebase/db/BakedBreadTags";

import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import HomeButton from "src/components/atoms/button/HomeButton";
import BakedBreadTagList from "src/components/organisms/list/BakedBreadTagList";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import Button from "src/components/atoms/button/Button";
import ErrorMessage from "src/components/atoms/form/ErrorMessage";

const mapEntityToState = (bakedBreadTags: BakedBreadTagEntity[]) => {
  const tags = bakedBreadTags.map(
    ({ id, name, order, is_featured, is_disabled }) => ({
      id,
      name,
      order,
      isFeatured: is_featured,
      isDisabled: is_disabled,
    })
  );

  return { tags };
};

const mapStateToEntity = (state: ReturnType<typeof mapEntityToState>) => {
  const tags = state.tags.map(
    ({ id, name, order, isFeatured, isDisabled }) => ({
      id,
      name,
      order,
      is_featured: isFeatured,
      is_disabled: isDisabled,
    })
  ) as BakedBreadTagEntity[];
  return tags;
};

type Props = DefaultPageProps &
  HistoryProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const BakedBreadTagIndexPage = (props: Props) => {
  const {
    isFetching,
    apiError,
    errors,
    tags,
    getBakedBreadTags,
    updateBakedBreadTags,
  } = props;

  const [state, setState] = useState(mapEntityToState(tags));
  const { tags: bakedBreadTags } = state;

  // 初期化
  useEffect(() => {
    getBakedBreadTags();
  }, [getBakedBreadTags]);

  useEffect(() => {
    if (tags.length) {
      setState(mapEntityToState(tags));
    }
  }, [tags]);

  // orderを元にtagの名称を更新
  const onChangeName = useCallback(
    (order: number, value: string) => {
      const index = bakedBreadTags.findIndex((v) => v.order === order);
      bakedBreadTags[index]["name"] = value;
      setState({ tags: bakedBreadTags });
    },
    [bakedBreadTags]
  );

  // orderを元にtagのラジオボタンを更新
  const onChangeRadio = useCallback(
    (key: "isFeatured" | "isDisabled", order: number, value: boolean) => {
      setState(() => {
        const index = bakedBreadTags.findIndex((v) => v.order === order);
        console.log(index);
        bakedBreadTags[index][key] = value;
        return { tags: bakedBreadTags };
      });
    },
    [bakedBreadTags]
  );

  // ソート順が変更された時
  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      setState(() => {
        const tags = arrayMove(bakedBreadTags, oldIndex, newIndex).map(
          (v, i) => ({
            ...v,
            order: i,
          })
        );
        return { tags };
      });
    },
    [bakedBreadTags]
  );

  // 追加ボタン
  const onAdd = useCallback(() => {
    setState(() => {
      const index = bakedBreadTags.length;
      bakedBreadTags.push({
        id: "",
        name: "",
        order: index,
        isFeatured: false,
        isDisabled: false,
      });
      return { tags: bakedBreadTags };
    });
  }, [bakedBreadTags]);

  // 削除ボタン
  const onDelete = useCallback((order: number) => {
    console.log(order);
    // setState(() => {
    //   const tags = bakedBreadTags
    //     .filter(v => v.order != order)
    //     .map((v, i) => ({
    //       ...v,
    //       order: i,
    //     }))
    //   return { tags }
    // });
  }, []);

  // 保存ボタン
  const onSubmit = useCallback(() => {
    const values = mapStateToEntity(state);
    updateBakedBreadTags(values);
  }, [state, updateBakedBreadTags]);

  const isDisabledSubmit = useMemo(() => state.tags.some(({ name }) => !name), [
    state,
  ]);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <HeadingWrapper>
          <Heading tag="h1" text="パン用のカテゴリをここから編集できます。" />
        </HeadingWrapper>
        {<ErrorMessage fontSize={16}>{apiError?.message}</ErrorMessage>}

        <BakedBreadTagList
          bakedBreadTags={bakedBreadTags}
          onChangeName={onChangeName}
          onChangeRadio={onChangeRadio}
          onDelete={onDelete}
          onSortEnd={onSortEnd}
          updateBeforeSortStart={(node) => {
            node.node.setAttribute("style", "z-index:9999;");
          }}
          errors={errors}
        />
        <button className="button-add" onClick={() => onAdd()}>
          <IoIosAdd size={24} />
        </button>
        <ButtonWrapper>
          <Button
            isDisabled={isDisabledSubmit}
            clickEvent={onSubmit}
            text={isDisabledSubmit ? "未入力の項目があります" : "保存する"}
          />
        </ButtonWrapper>
        <ButtonWrapper>
          {/* <LinkButton url={'/baked_bread_tags/create'} text="新しく登録する" isDisabled={false} /> */}
          <HomeButton />
        </ButtonWrapper>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M16} 0;

  .button-add {
    margin: 24px auto 12px;
    display: block;
    display: none;

    svg {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      color: #fff;
      background: ${StyleConfig.COLOR.PRIMARY};
    }
  }
`;
const HeadingWrapper = styled.div`
  padding: 0 ${StyleConfig.HELPER.M16};
`;
const ButtonWrapper = styled.div`
  padding: 0 ${StyleConfig.HELPER.M16};
  margin-top: 32px;
  > * {
    margin: ${StyleConfig.HELPER.M16} auto;
  }
`;

const mapStateToProps = (state: State) => ({
  tags: state.bakedBreadTag.resource,
  isFetching: state.bakedBreadTag.isFetching,
  errors: state.bakedBreadTag.validationErrors,
  apiError: state.bakedBreadTag.apiError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getBakedBreadTags: () => dispatch(bakedBreadTagActions.getBakedBreadTags()),
  updateBakedBreadTags: (tags: BakedBreadTagEntity[]) =>
    dispatch(bakedBreadTagActions.updateBakedBreadTags(tags)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BakedBreadTagIndexPage);
